import * as Sentry from '@sentry/react';

const ignoreErrors = ["Can't find variable: clarity", 'ReferenceError: clarity is not defined'];

// Drop the PHI query params (passed in embedded sign-up — see useEmbeddedChat.ts) from any
// URL-bearing field, keeping the rest of the URL. Values without them pass through unchanged.
const PHI_PARAMS = ['email', 'country', 'diseases'];
export const stripPhi = (value?: unknown) => {
  if (typeof value !== 'string') return value as undefined;
  try {
    const parsed = new URL(value, 'http://x');
    if (!PHI_PARAMS.some((param) => parsed.searchParams.has(param))) return value;
    PHI_PARAMS.forEach((param) => parsed.searchParams.delete(param));
    // strip the dummy base we added for relative URLs
    return value.startsWith('http') ? parsed.href : parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return value;
  }
};

// iOS Chrome injects its own script into the page (page translation). That script overflows the
// stack and its frames carry the page URL, never a file under /assets/. An overflow with at least
// one frame, none of them in our bundle, is not ours.
export const isForeignStackOverflow = (event: Sentry.Event) => {
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];
  const isOverflow = event.exception?.values?.some((value) =>
    value.value?.includes('Maximum call stack size exceeded'),
  );
  return !!isOverflow && frames.length > 0 && !frames.some((f) => f.filename?.includes('/assets/'));
};

const scrubPhi = (event: Sentry.Event): Sentry.Event => {
  const request = event.request && {
    ...event.request,
    url: stripPhi(event.request.url),
    // query_string is a bare `a=1&b=2` (not a URL) and rarely set by the browser SDK — just drop it.
    query_string: undefined,
    // drop Authorization; strip PHI from URL-bearing headers (Referer/Origin carry /sign-up?email=...)
    headers:
      event.request.headers &&
      Object.fromEntries(
        Object.entries(event.request.headers)
          .filter(([key]) => key.toLowerCase() !== 'authorization')
          .map(([key, val]) => [
            key,
            ['referer', 'origin'].includes(key.toLowerCase()) ? (stripPhi(val) as string) : val,
          ]),
      ),
  };
  // The email lands in the address bar (/sign-up?email=...), so scrub the page URL wherever it
  // appears: request.url above, navigation breadcrumbs (data.from/to) and fetch breadcrumbs (data.url).
  const breadcrumbs = event.breadcrumbs?.map((crumb) =>
    crumb.data
      ? {
          ...crumb,
          data: {
            ...crumb.data,
            url: stripPhi(crumb.data.url),
            from: stripPhi(crumb.data.from),
            to: stripPhi(crumb.data.to),
          },
        }
      : crumb,
  );
  return { ...event, request, breadcrumbs };
};

// `beforeSend` never sees replay events, so their page URL list is scrubbed here.
export const scrubReplayEvent = (event: Sentry.Event): Sentry.Event =>
  event.type === 'replay_event'
    ? ({
        ...scrubPhi(event),
        urls: (event as { urls?: string[] }).urls?.map(stripPhi),
      } as Sentry.Event)
    : event;

const stripPhiFields = (obj: unknown) =>
  obj && typeof obj === 'object'
    ? {
        ...obj,
        ...Object.fromEntries(
          ['href', 'url', 'from', 'to', 'description']
            .filter((key) => key in obj)
            .map((key) => [key, stripPhi((obj as Record<string, unknown>)[key])]),
        ),
      }
    : obj;

// The recording carries page URLs in the meta event (data.href) and in the breadcrumb and span
// custom events (data.payload). DOM snapshots are left alone: text and inputs are already masked.
// Sentry's `beforeAddRecordingEvent` skips the meta event, so `scrubEventBuffers` applies this instead.
const RRWEB_META = 4;
const RRWEB_CUSTOM = 5;
export const scrubRecordingEvent = <T extends { data: unknown; type: number }>(event: T): T => {
  if (event.type === RRWEB_META) return { ...event, data: stripPhiFields(event.data) };
  if (event.type !== RRWEB_CUSTOM) return event;
  const data = event.data as { payload?: { data?: unknown } };
  const payload = stripPhiFields(data.payload) as { data?: unknown } | undefined;
  return {
    ...event,
    data: { ...data, payload: payload && { ...payload, data: stripPhiFields(payload.data) } },
  };
};

type Recordable = { addEvent: (event: never) => unknown };
type ReplayContainer = { eventBuffer: Recordable | null };

// Sentry 7 runs `beforeAddRecordingEvent` on custom events only, so the rrweb meta event (with
// `window.location.href`) would reach the recording unscrubbed. Every event goes through the
// container's `eventBuffer`, which Sentry replaces on each new session: wrap each one it assigns.
// Relies on private fields of @sentry/replay 7.x.
export const scrubEventBuffers = (container: ReplayContainer) => {
  const wrap = (buffer: Recordable | null) => {
    if (!buffer) return buffer;
    const add = buffer.addEvent.bind(buffer);
    return Object.assign(buffer, {
      addEvent: (event: never) => add(scrubRecordingEvent(event as never) as never),
    });
  };
  let current = wrap(container.eventBuffer);
  Object.defineProperty(container, 'eventBuffer', {
    configurable: true,
    get: () => current,
    set: (next: Recordable | null) => {
      current = wrap(next);
    },
  });
};

export const sentryInit = () => {
  const replay = Sentry.replayIntegration({
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    maxReplayDuration: 900000, // 15 minutes max (default is 1 hour)
    minReplayDuration: 5000, // 5 seconds min before sending
  });
  Sentry.init({
    // Only report errors from our own bundle. Scripts the browser injects (for example Chrome
    // translate on iOS) show up as frames on the page URL, with minified names we cannot read.
    allowUrls: [/\/assets\/.*\.js/],
    beforeSend: (event) => {
      if (
        ignoreErrors.some((error) => event?.message?.toLowerCase().includes(error.toLowerCase()))
      ) {
        return null;
      }
      if (isForeignStackOverflow(event)) return null;
      return scrubPhi(event);
    },
    beforeSendTransaction: scrubPhi,
    dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration(), replay],
    // Performance Monitoring
    tracesSampleRate: 0.1, //  Capture 100% of the transactions

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost'],

    // Session Replay
    replaysSessionSampleRate: 0, // Don't record all sessions - only capture when errors occur
    replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions where errors occur
  });
  Sentry.addEventProcessor(scrubReplayEvent);
  // eslint-disable-next-line no-underscore-dangle
  const container = (replay as unknown as { _replay?: ReplayContainer })._replay;
  // No container when Sentry is disabled (no DSN): nothing is recorded.
  if (container) scrubEventBuffers(container);
};
